import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shared presentational primitives.
 *
 * Icons are inline SVG rather than an icon package: this bundle ships with no
 * runtime dependencies and loads inside the DSH shell, where a webfont request
 * would be a second network round trip for decoration. Glyphs are Lucide
 * paths on a 24-unit grid at stroke-width 2.75 — the rounder, heavier weight
 * the market design language calls for.
 */
import { useEffect, useRef, useState } from 'react';
import css from './ui.module.css';
/** Tier to locale key. */
export const TIER_KEYS = {
    'verified-npm': 'tierVerifiedNpm',
    'verified-git': 'tierVerifiedGit',
    'likely-plugin': 'tierLikely',
    related: 'tierRelated',
};
/** Closed-vocabulary tags the UI speaks natively; anything else falls back to the raw tag. */
const TAG_KEYS = {
    'has-web-ui': 'badgeWebUi',
    skills: 'badgeSkills',
    'needs-api-key': 'badgeApiKey',
    memory: 'capMemory',
    'multi-agent': 'capMultiAgent',
    'web-search': 'capWebSearch',
    'mcp-bridge': 'capMcpBridge',
    'cli-companion': 'capCliCompanion',
    ocr: 'capOcr',
    'file-ops': 'capFileOps',
};
/** Category to locale key, covering the crawler's whole category vocabulary. */
const CATEGORY_KEYS = {
    coding: 'catCoding',
    'browser-web': 'catBrowserWeb',
    'data-analysis': 'catDataAnalysis',
    'media-image': 'catMediaImage',
    'media-audio-video': 'catMediaAudioVideo',
    productivity: 'catProductivity',
    communication: 'catCommunication',
    'devops-infra': 'catDevopsInfra',
    'search-knowledge': 'catSearchKnowledge',
    'ui-experience': 'catUiExperience',
    'agent-orchestration': 'catAgentOrchestration',
    security: 'catSecurity',
    other: 'catOther',
};
/**
 * Translate a capability tag from the closed vocabulary.
 * @param t - the bound translation function.
 * @param tag - the raw tag.
 * @returns the localized label, or the tag itself when unknown.
 */
export function tagLabel(t, tag) {
    const key = TAG_KEYS[tag];
    return key === undefined ? tag : t(key);
}
/**
 * Translate a category from the closed vocabulary.
 * @param t - the bound translation function.
 * @param category - the raw category.
 * @returns the localized label, or the category itself when unknown.
 */
export function categoryLabel(t, category) {
    const key = CATEGORY_KEYS[category];
    return key === undefined ? category : t(key);
}
/** Lucide path data, drawn on a 24-unit grid. */
const GLYPHS = {
    star: _jsx("path", { d: "m12 3 2.7 5.7 6.3.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.3-.9z" }),
    close: _jsx("path", { d: "M18 6 6 18M6 6l12 12" }),
    external: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M15 3h6v6" }), _jsx("path", { d: "M10 14 21 3" }), _jsx("path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" })] })),
    search: (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "11", cy: "11", r: "7" }), _jsx("path", { d: "m20 20-3.5-3.5" })] })),
    refresh: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M21 12a9 9 0 1 1-2.64-6.36" }), _jsx("path", { d: "M21 3v6h-6" })] })),
    back: _jsx("path", { d: "m15 18-6-6 6-6" }),
    down: _jsx("path", { d: "m6 9 6 6 6-6" }),
    empty: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" }), _jsx("path", { d: "m3.3 7 8.7 5 8.7-5" }), _jsx("path", { d: "M12 22V12" })] })),
    copy: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M9 9h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2z" }), _jsx("path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" })] })),
    check: _jsx("path", { d: "M20 6 9 17l-5-5" }),
    shield: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M12 2 4 6v6c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6z" }), _jsx("path", { d: "m9 12 2 2 4-4" })] })),
    file: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" }), _jsx("path", { d: "M14 3v5h5" }), _jsx("path", { d: "M9 13h6M9 17h4" })] })),
    info: (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "12", cy: "12", r: "9" }), _jsx("path", { d: "M12 8h.01M11 12h1v4h1" })] })),
};
/** Icons drawn with a solid fill rather than a stroke. */
const FILLED = new Set(['star']);
/**
 * Draw one icon.
 * @param props - the icon name and size.
 * @returns the SVG element.
 */
export function Icon(props) {
    const { name, size } = props;
    const filled = FILLED.has(name);
    return (_jsx("svg", { className: css.icon, width: size ?? '1em', height: size ?? '1em', viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", focusable: "false", stroke: filled ? 'none' : 'currentColor', strokeWidth: 2.75, strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("g", { fill: filled ? 'currentColor' : 'none', children: GLYPHS[name] }) }));
}
/**
 * Copy-to-clipboard button for terminal surfaces.
 *
 * The feedback is the icon and label swapping to a check for a moment: a
 * toast would be a second notification system for a one-word message. When
 * the Clipboard API is unavailable (insecure context) the click is a no-op.
 * @param props - the text and its labels.
 * @returns the button.
 */
export function CopyButton(props) {
    const { text, label, copiedLabel } = props;
    const [copied, setCopied] = useState(false);
    const timer = useRef(undefined);
    useEffect(() => () => { window.clearTimeout(timer.current); }, []);
    const copy = () => {
        if (navigator.clipboard === undefined)
            return;
        void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => { setCopied(false); }, 1600);
        });
    };
    return (_jsxs("button", { type: "button", className: css.copyButton, "data-copied": copied ? '' : undefined, onClick: copy, title: copied ? copiedLabel : label, "aria-label": copied ? copiedLabel : label, children: [_jsx(Icon, { name: copied ? 'check' : 'copy', size: 13 }), _jsx("span", { children: copied ? copiedLabel : label })] }));
}
/**
 * A dark terminal surface for commands, in both themes.
 *
 * The copy button rides the top-right corner: "a command you run" stays one
 * click away without a separate head bar eating vertical space. The body is a
 * `<pre>`, so multi-line manual steps keep their line breaks.
 * @param props - the command and its copy labels.
 * @returns the terminal block.
 */
export function Terminal(props) {
    const { command, copyLabel, copiedLabel, className } = props;
    return (_jsxs("div", { className: className === undefined ? css.terminal : `${css.terminal} ${className}`, children: [_jsx("pre", { className: css.terminalBody, children: command }), _jsx(CopyButton, { text: command, label: copyLabel, copiedLabel: copiedLabel })] }));
}
/**
 * One label-over-value pair in the metrics grid.
 * @param props - the label and value.
 * @returns the metric cell.
 */
export function Metric(props) {
    const { label, value } = props;
    return (_jsxs("div", { className: css.metric, children: [_jsx("dt", { className: css.metricLabel, children: label }), _jsx("dd", { className: css.metricValue, children: typeof value === 'number' ? compact(value) : value })] }));
}
/**
 * Shorten a large count so columns stay aligned.
 * @param value - the raw count.
 * @returns a display string.
 */
export function compact(value) {
    if (value >= 10_000)
        return `${(value / 1000).toFixed(0)}k`;
    if (value >= 1000)
        return `${(value / 1000).toFixed(1)}k`;
    return String(value);
}
/**
 * Format an ISO timestamp as a short local date.
 * @param iso - the timestamp.
 * @returns a date string, or an em dash when unparsable.
 */
export function shortDate(iso) {
    const value = Date.parse(iso);
    if (Number.isNaN(value))
        return '—';
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
/**
 * Describe how long ago a timestamp was, in coarse buckets.
 *
 * Freshness is the point, not precision: "3 months ago" tells you whether a
 * plugin is maintained, and an exact date does not.
 * @param iso - the timestamp.
 * @param t - the bound translation function.
 * @returns a relative description.
 */
export function relativeAge(iso, t) {
    const value = Date.parse(iso);
    if (Number.isNaN(value))
        return '—';
    const days = Math.floor((Date.now() - value) / 86_400_000);
    if (days <= 1)
        return t('ageToday');
    if (days < 30)
        return `${days}${t('ageDays')}`;
    if (days < 365)
        return `${Math.floor(days / 30)}${t('ageMonths')}`;
    return `${Math.floor(days / 365)}${t('ageYears')}`;
}
